import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed } from 'vue';

import {
  CreateMessengerChannelDocument,
  DeleteMessengerChannelDocument,
  GetMessengerChannelsDocument,
  UpdateMessengerChannelDocument,
} from '@shared/api/graphql/gql/graphql';

import type { MessengerChannel, UpdateChannelData } from './types';

import { messengerQueryNames } from '../api/messenger-query-names';

/**
 * credentials-часть input мутации (group_id → number). Плоский набор строковых
 * ключей из формы создания/обновления канала.
 */
interface ChannelCredentialsInput {
  token?: string;
  access_token?: string;
  group_id?: number;
  confirmation?: string;
  secret?: string;
  screen_name?: string;
}

/**
 * Плоский источник credentials (форма создания или UpdateChannelData) — только
 * строковые поля читаются, is_active игнорируется билдером.
 */
interface CredentialsSource {
  token?: string;
  access_token?: string;
  group_id?: string;
  confirmation?: string;
  secret?: string;
  screen_name?: string;
}

const REFETCH_CHANNELS = [messengerQueryNames.getMessengerChannels];

/**
 * Собирает credentials-часть input из плоского словаря (group_id → number).
 */
function buildCredentialsInput(source: CredentialsSource): ChannelCredentialsInput {
  const input: ChannelCredentialsInput = {};

  if (source.token) {
    input.token = source.token;
  }
  if (source.access_token) {
    input.access_token = source.access_token;
  }
  if (source.group_id) {
    input.group_id = Number(source.group_id);
  }
  if (source.confirmation) {
    input.confirmation = source.confirmation;
  }
  if (source.secret) {
    input.secret = source.secret;
  }
  if (source.screen_name) {
    input.screen_name = source.screen_name;
  }

  return input;
}

/**
 * Composable для управления каналами мессенджера (GraphQL/Apollo).
 * Типы каналов — из codegen (id как есть), без ручных мапперов.
 * Публичная сигнатура сохранена — потребители модалок/форм не меняются.
 */
export const useMessengerChannels = () => {
  const channelsQuery = useQuery(GetMessengerChannelsDocument);

  const createMutation = useMutation(CreateMessengerChannelDocument, { refetchQueries: REFETCH_CHANNELS });
  const updateMutation = useMutation(UpdateMessengerChannelDocument, { refetchQueries: REFETCH_CHANNELS });
  const deleteMutation = useMutation(DeleteMessengerChannelDocument, { refetchQueries: REFETCH_CHANNELS });

  /**
   * Список каналов
   */
  const channels = computed<MessengerChannel[]>(() => channelsQuery.result.value?.messengerChannels ?? []);

  /**
   * Отсортированные каналы (активные сначала, затем по приоритету)
   */
  const sortedChannels = computed(() => {
    return [...channels.value].sort((a, b) => {
      if (a.is_active && !b.is_active) return -1;
      if (!a.is_active && b.is_active) return 1;
      return a.priority - b.priority;
    });
  });

  /**
   * Активные каналы
   */
  const activeChannels = computed(() => {
    return channels.value.filter((ch) => ch.is_active);
  });

  /**
   * Статус здоровья всех каналов
   * @returns 'online' | 'offline' | 'warning' | 'empty'
   */
  const overallHealthStatus = computed(() => {
    if (activeChannels.value.length === 0) {
      return 'empty';
    }

    const hasOffline = activeChannels.value.some((ch) => ch.health_status.status === 'offline');
    if (hasOffline) return 'offline';

    const hasWarning = activeChannels.value.some((ch) => ch.health_status.status === 'warning');
    if (hasWarning) return 'warning';

    return 'online';
  });

  /**
   * Состояние загрузки
   */
  const isLoading = computed(() => channelsQuery.loading.value);

  /**
   * Ошибка загрузки
   */
  const error = computed(() => channelsQuery.error.value);

  /**
   * Создать новый канал
   */
  const createChannel = async (credentials: Record<string, string>, provider: string): Promise<MessengerChannel> => {
    const response = await createMutation.mutate({
      input: { provider, ...buildCredentialsInput(credentials) },
    });

    return response?.data?.createMessengerChannel ?? ({} as MessengerChannel);
  };

  /**
   * Обновить канал
   */
  const updateChannel = async (id: string, data: UpdateChannelData): Promise<MessengerChannel> => {
    const input: ChannelCredentialsInput & { is_active?: boolean } = buildCredentialsInput(data);
    if (data.is_active !== undefined) {
      input.is_active = data.is_active;
    }

    const response = await updateMutation.mutate({ id, input });

    return response?.data?.updateMessengerChannel ?? ({} as MessengerChannel);
  };

  /**
   * Удалить канал
   */
  const deleteChannel = async (id: string): Promise<void> => {
    await deleteMutation.mutate({ id });
  };

  /**
   * Обновить список каналов
   */
  const refetch = async () => {
    await channelsQuery.refetch();
  };

  return {
    // Данные
    channels,
    sortedChannels,
    activeChannels,
    overallHealthStatus,

    // Состояние
    isLoading,
    error,
    isCreating: createMutation.loading,
    isUpdating: updateMutation.loading,
    isDeleting: deleteMutation.loading,

    // Методы
    createChannel,
    updateChannel,
    deleteChannel,
    refetch,
  };
};
